import {
  Box,
  Flex,
  Tag,
  TagCloseButton,
  TagLabel,
  Input,
  List,
  ListItem,
  useOutsideClick,
} from "@chakra-ui/react";
import React, { useState, useRef } from "react";

interface MultipleSelectProps {
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  maxWidth?: string | number;
  maxHeight?: string | number;
  backgroundColor?: string;
  width?: string | number;
}

const MultipleSelect: React.FC<MultipleSelectProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = "Selecione os itens...",
  maxWidth,
  maxHeight,
  backgroundColor,
  width,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useOutsideClick({
    ref: ref,
    handler: () => setIsOpen(false),
  });

  const filteredOptions = options.filter(
    (option) =>
      option.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !selectedValues.includes(option)
  );

  const handleSelect = (option: string) => {
    onChange([...selectedValues, option]);
    setSearchTerm("");
  };

  const handleRemove = (optionToRemove: string) => {
    onChange(selectedValues.filter((option) => option !== optionToRemove));
  };

  return (
    <Box ref={ref} position="relative">
      <Box
        border="1px"
        borderColor="gray.200"
        borderRadius="md"
        p={2}
        onClick={() => setIsOpen(true)}
        maxW={maxWidth}
        h={maxHeight}
        overflowY="auto"
        backgroundColor={backgroundColor || "white"}
        width={width}
      >
        <Flex flexWrap="wrap" gap={2}>
          {selectedValues.map((value) => (
            <Tag
              key={value}
              size="md"
              borderRadius="full"
              variant="solid"
              colorScheme="blue"
            >
              <TagLabel>{value}</TagLabel>
              <TagCloseButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(value);
                }}
              />
            </Tag>
          ))}
          <Input
            variant="unstyled"
            placeholder={selectedValues.length === 0 ? placeholder : ""}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="sm"
            flex={1}
            minW="120px"
          />
        </Flex>
      </Box>
      {isOpen && (
        <List
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={2}
          maxH="200px"
          overflowY="auto"
          bg="white"
          borderRadius="md"
          boxShadow="lg"
          zIndex={10}
        >
          {filteredOptions.map((option) => (
            <ListItem
              key={option}
              px={4}
              py={2}
              cursor="pointer"
              _hover={{ bg: "gray.100" }}
              onClick={() => handleSelect(option)}
            >
              {option}
            </ListItem>
          ))}
          {filteredOptions.length === 0 && (
            <ListItem px={4} py={2} color="gray.500">
              Não há mais opções disponíveis
            </ListItem>
          )}
        </List>
      )}
    </Box>
  );
};

export default MultipleSelect;
